import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { addCustomer, loginCustomer, resetCustomerState } from "../redux/AuthSlice";
import { api_url_v1 } from "../utils/config";

const AuthModal = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const { loading, error, success } = useSelector((state) => state.customer);
  
  const [isRegister, setIsRegister] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState("");
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
  });

  // useEffect(() => {
  //   if (success) {
  //     setFormData({
  //       firstName: "",
  //       lastName: "",
  //       email: "",
  //       password: "",
  //     });
  //     setTimeout(() => {
  //       onClose();
  //       dispatch(resetCustomerState());
  //     }, 1500);
  //   }
  // }, [success, onClose, dispatch]);
  useEffect(() => {
    if (success) {
      if (isRegister) {
        // After successful registration, switch to login mode
        setTimeout(() => {
          setIsRegister(false);
          dispatch(resetCustomerState());
          setFormData({
            firstName: "",
            lastName: "",
            email: formData.email, // Keep email for convenience
            password: "",
          });
        }, 2500); // Give user time to read the message
      } else {
        // After successful login, close modal
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          password: "",
        });
        setTimeout(() => {
          onClose();
          dispatch(resetCustomerState());
        }, 1500);
      }
    }
  }, [success, onClose, dispatch, isRegister]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isRegister) {
      const customerData = {
        name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        // phone: "", 
        pword: formData.password,
      };
      dispatch(addCustomer(customerData));
    } else {
      const loginData = {
        email: formData.email,
        pword: formData.password,
      };
      dispatch(loginCustomer(loginData));
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotPasswordLoading(true);
    setForgotPasswordError("");
    setForgotPasswordSuccess(false);

    try {
      const response = await fetch(`${api_url_v1}/forgotPassword`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: forgotPasswordEmail }),
      });

      const data = await response.json();

      if (response.ok) {
        setForgotPasswordSuccess(true);
        setForgotPasswordEmail("");
        setTimeout(() => {
          setIsForgotPassword(false);
          setForgotPasswordSuccess(false);
        }, 3000);
      } else {
        setForgotPasswordError(data.message || "Failed to send reset email");
      }
    } catch (err) {
      setForgotPasswordError("An error occurred. Please try again.");
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setIsForgotPassword(false);
    setForgotPasswordEmail("");
    setForgotPasswordError("");
    setForgotPasswordSuccess(false);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black/40 flex justify-center items-center z-[999]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Modal Card */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 40 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 40 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
            className="bg-white shadow-xl w-[90%] sm:w-[400px] p-8 relative font-garamond"
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-xl"
              disabled={loading || forgotPasswordLoading}
            >
              x
            </button>

            {/* Header */}
            <h2 className="text-2xl font-semibold text-gray-800 mb-6 text-center">
              {isForgotPassword 
                ? "Reset Password" 
                : isRegister 
                ? "Create Account" 
                : "Sign In"}
            </h2>

            {/* Forgot Password View */}
            {isForgotPassword ? (
              <>
                {/* Success Message */}
                {forgotPasswordSuccess && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 mb-4 text-sm text-center"
                  >
                    Password reset link sent! Check your email.
                  </motion.div>
                )}

                {/* Error Message */}
                {forgotPasswordError && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 mb-4 text-sm text-center"
                  >
                    {forgotPasswordError}
                  </motion.div>
                )}

                <p className="text-sm text-gray-600 mb-4 text-center">
                  Enter your email address and we'll send you a link to reset your password.
                </p>

                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600">
                      Email
                    </label>
                    <input
                      type="email"
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      className="w-full px-3 py-2 border-b text-sm outline-none"
                      required
                      disabled={forgotPasswordLoading}
                    />
                  </div>

                  <motion.button
                    whileHover={!forgotPasswordLoading ? { scale: 1.02 } : {}}
                    whileTap={!forgotPasswordLoading ? { scale: 0.97 } : {}}
                    type="submit"
                    disabled={forgotPasswordLoading}
                    className="w-full bg-[#DDC57A] text-white py-2 mt-4 text-sm font-semibold hover:bg-amber-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {forgotPasswordLoading ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Sending...
                      </>
                    ) : (
                      "Send Reset Link"
                    )}
                  </motion.button>
                </form>

                <p className="text-center text-sm text-gray-500 mt-6">
                  <button
                    onClick={handleBackToLogin}
                    className="text-[#DDC57A] font-medium hover:underline"
                    disabled={forgotPasswordLoading}
                  >
                    Back to Sign In
                  </button>
                </p>
              </>
            ) : (
              <>
                {/* Success Message */}
                {success && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 mb-4 text-sm text-center"
                  >
                    {isRegister ? "Account created successfully! Please sign in with your email and password." : "Login successful!"}
                  </motion.div>
                )}

                {/* Error Message */}
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 mb-4 text-sm text-center"
                  >
                    {error}
                  </motion.div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  {isRegister && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-600">
                          First Name
                        </label>
                        <input
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border-b text-sm outline-none"
                          required
                          disabled={loading}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-600">
                          Last Name
                        </label>
                        <input
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border-b text-sm outline-none"
                          required
                          disabled={loading}
                        />
                      </div>
                    </>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-600">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border-b text-sm outline-none"
                      required
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-600">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border-b text-sm outline-none pr-10"
                        required
                        disabled={loading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                        disabled={loading}
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Forgot Password Link */}
                  {!isRegister && (
                    <div className="text-right">
                      <button
                        type="button"
                        onClick={() => setIsForgotPassword(true)}
                        className="text-sm text-[#DDC57A] hover:underline"
                        disabled={loading}
                      >
                        Forgot Password?
                      </button>
                    </div>
                  )}

                  <motion.button
                    whileHover={!loading ? { scale: 1.02 } : {}}
                    whileTap={!loading ? { scale: 0.97 } : {}}
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#DDC57A] text-white py-2 mt-4 text-sm font-semibold hover:bg-amber-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {loading ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Processing...
                      </>
                    ) : (
                      <>{isRegister ? "Register" : "Sign In"}</>
                    )}
                  </motion.button>
                </form>

                {/* Toggle */}
                <p className="text-center text-sm text-gray-500 mt-6">
                  {isRegister ? "Already have an account?" : "Don't have an account?"}{" "}
                  <button
                    onClick={() => {
                      setIsRegister(!isRegister);
                      dispatch(resetCustomerState());
                    }}
                    className="text-[#DDC57A] font-medium hover:underline"
                    disabled={loading}
                  >
                    {isRegister ? "Sign In" : "Register"}
                  </button>
                </p>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AuthModal;